import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import {
  API,
  ChannelDeviceStatusDTO,
  LocalizedFramedReportingDTO,
  LocalizedFrameDTO,
  LocationStatusDTO,
  Weekday,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { SbDateTime, SbLocalDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { XYChartModel } from '@ngx-seatback/charts';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { forkJoin, Subscription } from 'rxjs';

@Component({
  selector: 'sb-network-overview-reporting',
  templateUrl: './network-overview-reporting.component.html',
  styleUrls: ['./network-overview-reporting.component.scss'],
  standalone: false,
})
export class NetworkOverviewReportingComponent implements OnInit, OnDestroy {
  deviceData: ChannelDeviceStatusDTO[];
  networkData: ChannelDeviceStatusDTO;
  locationData: LocationStatusDTO;
  date: SbDateTime;
  broadcastingRate = 0;
  player = 0;
  subs: Subscription[] = [];

  broadcastingGradient: XYChartModel = {
    data: undefined,
    height: 0,
    loadingDone: false,
    title: '',
    valueAssignments: [],
  };

  constructor(
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.date = new SbDateTime(DateTime.local());
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
    const pFrom = new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 91 }));
    const pTill = new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
    const sub = forkJoin({
      deviceData: this.api.ss_nm_Network_channelDeviceStatus({
        productive: true,
      }),
      channelDeviceStatus: this.api.ss_nm_Network_deviceStatus({
        productive: true,
      }),
      locationStatus: this.api.ss_nm_Network_locationStatus(),
      airtimeReporting: this.api.ss_nm_Reporting_airtimeQuota({
        from: pFrom,
        till: pTill,
        productive: true,
        interval: 604800,
        weekday: [Weekday.MONDAY, Weekday.TUESDAY, Weekday.WEDNESDAY, Weekday.THURSDAY, Weekday.FRIDAY, Weekday.SATURDAY],
        hour: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: {
          deviceData: ChannelDeviceStatusDTO[];
          channelDeviceStatus: ChannelDeviceStatusDTO;
          locationStatus: LocationStatusDTO;
          airtimeReporting: LocalizedFramedReportingDTO;
        }) => {
          this.deviceData = data.deviceData;
          this.networkData = data.channelDeviceStatus;
          this.locationData = data.locationStatus;
          this.initChartData(data.airtimeReporting.buckets[0].frames);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  initChartData(data: LocalizedFrameDTO[]) {
    const broadcastingChartData: { date: DateTime; value: number; devices: number }[] = [];

    for (const i of data) {
      broadcastingChartData.push({
        date: i.range.from.value,
        value: Math.round(i.data.quota * 100),
        devices: Math.round(i.data.devices),
      });
    }

    this.broadcastingRate = broadcastingChartData[broadcastingChartData.length - 1].value;
    this.player = broadcastingChartData[broadcastingChartData.length - 1].devices;

    this.broadcastingGradient = {
      title: 'Broadcasting Quota for last 7 Days',
      height: 150,
      valueAssignments: [
        {
          value: 'value',
          categories: ['date'],
          color: '#0194dc',
          valueName: 'Quota',
        },
        {
          value: 'devices',
          categories: ['date'],
          color: 'rgba(215,219,220,0.43)',
          valueName: 'Player',
          ownAxis: true,
        },
      ],
      categoryType: 'date',
      fill: true,
      legend: false,
      showBullet: false,
      disableAxis: true,
      disableLabel: true,
      startAtBorder: true,
      gradient: true,
      data: broadcastingChartData,
      loadingDone: true,
    };
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }
}
